/**
 * Typed input: what a key means, and the accent bar the software keyboard will not give us
 * (TASKS T48, SPEC §2 `input/keyboard.ts`, SPEC §4).
 *
 * Three things live here, and they are here together because they are one subject — *typing a
 * Spanish answer on a phone at 3:40 a.m.* — approached from three directions.
 *
 *  1. **`keyAction`** — the physical-keyboard policy, as a pure function. It was inline in `app.ts`,
 *     where "does Space submit?" was answerable only by booting a browser. SPEC §4 gives the PC
 *     three bindings and they are worth a table, not a nest of `if`s in a listener.
 *  2. **The accent bar** — `á é í ó ú ñ ü ¿ ¡`, mounted as real buttons. iOS long-press is
 *     explicitly not relied upon (SPEC §4): it needs a half-second hold and a second precise tap on
 *     a popover, with one thumb, in the dark. Getting `ñ` wrong is not a Spanish error and must not
 *     be scored as one.
 *  3. **`keyboardInsetPx` and `KeyboardInsetWatcher`** — where the software keyboard actually is.
 *     "Above the keyboard" is not a thing CSS knows: the software keyboard does not shrink the
 *     layout viewport on iOS, so an in-flow bar sits *behind* it. The visual viewport is the only
 *     thing that reports the keyboard's height, and this turns it into one custom property the
 *     stylesheet can pin to.
 *
 * Nothing here holds game state or touches the store. The bar reports characters; the caller owns
 * the draft.
 */

import type { Phase, RunPhase } from '../state/gamestate';
import { button, el } from '../ui/shell';

// ---------------------------------------------------------------------------------------------
// The accent bar
// ---------------------------------------------------------------------------------------------

export interface AccentKey {
  char: string;
  /** Spoken by a screen reader. English: the reader is an English speaker learning Spanish. */
  label: string;
}

/**
 * The nine characters, in the order SPEC §4 lists them: five accented vowels, ñ, ü, then the two
 * opening marks.
 *
 * Vowels first because they are what a production answer needs most often, and left-to-right in
 * alphabetical order so the position of each is learnable — a bar whose keys move is a bar the
 * learner has to read every time.
 */
export const ACCENT_KEYS: readonly AccentKey[] = Object.freeze([
  { char: 'á', label: 'a with acute accent' },
  { char: 'é', label: 'e with acute accent' },
  { char: 'í', label: 'i with acute accent' },
  { char: 'ó', label: 'o with acute accent' },
  { char: 'ú', label: 'u with acute accent' },
  { char: 'ñ', label: 'n with tilde' },
  { char: 'ü', label: 'u with diaeresis' },
  { char: '¿', label: 'opening question mark' },
  { char: '¡', label: 'opening exclamation mark' },
] as const);

export interface Spliced {
  value: string;
  /** Where the caret belongs afterwards: immediately after what was inserted. */
  caret: number;
}

/**
 * Insert `insert` over the range `[start, end)` of `value`.
 *
 * Pure, and separate from any DOM, because the caret arithmetic is the part that goes wrong: an
 * accent key that always appends turns `mama` + `á` into `mamaá` when the learner meant to fix the
 * second `a`. Out-of-range and reversed selections are normalised rather than rejected — a browser
 * can report `selectionStart > selectionEnd` after a backwards drag, and losing the answer to that
 * would be absurd.
 */
export function spliceAtCaret(
  value: string,
  start: number,
  end: number,
  insert: string,
): Spliced {
  const lo = Math.max(0, Math.min(value.length, Math.min(start, end)));
  const hi = Math.max(0, Math.min(value.length, Math.max(start, end)));
  return {
    value: value.slice(0, lo) + insert + value.slice(hi),
    caret: lo + insert.length,
  };
}

/**
 * Type `char` into `input` at the caret, and leave the caret after it.
 *
 * Returns the new value rather than relying on an `input` event: the value is set programmatically,
 * which fires nothing, and the caller's draft has to be updated from something. The field is
 * refocused because a learner who taps `ñ` intends to keep typing.
 */
export function insertAtCaret(input: HTMLInputElement, char: string): string {
  const { value, caret } = spliceAtCaret(
    input.value,
    input.selectionStart ?? input.value.length,
    input.selectionEnd ?? input.value.length,
    char,
  );
  input.value = value;
  input.focus();
  input.setSelectionRange(caret, caret);
  return value;
}

export interface AccentBarSpec {
  /** The field these keys type into. Resolved at tap time, not at build time. */
  field: () => HTMLInputElement | null;
  /** The field's new value, after the character was inserted. */
  onInsert: (value: string) => void;
  testId?: string;
  keys?: readonly AccentKey[];
  doc?: Document;
}

/**
 * The accent bar: nine buttons that type into the answer field without taking focus from it.
 *
 * `preventDefault` on the *pointer-down*, not on the click, is what makes it work. A button that
 * takes focus dismisses the iOS keyboard on the way in and raises it again on the way out — the
 * layout jumps twice per accent, and on a slow phone the second tap lands on whatever moved into
 * that spot. Preventing the default of the pointer event stops focus moving at all, so the keyboard
 * never goes down. `mousedown` is prevented too: a desktop browser driving a synthetic click has no
 * pointer event to cancel.
 */
export function accentBar(spec: AccentBarSpec): HTMLElement {
  const doc = spec.doc ?? document;
  const bar = el('div', 'accent-bar', doc);
  bar.dataset['testid'] = spec.testId ?? 'accent-bar';
  // A group, not a toolbar: these are nine equal keys, and `toolbar` would make a screen reader
  // promise arrow-key navigation between them that nothing implements.
  bar.setAttribute('role', 'group');
  bar.setAttribute('aria-label', 'Spanish characters');

  for (const key of spec.keys ?? ACCENT_KEYS) {
    const node = button(
      {
        label: key.char,
        testId: `accent-${key.char}`,
        ariaLabel: key.label,
        onClick: () => {
          const field = spec.field();
          if (!field) return;
          spec.onInsert(insertAtCaret(field, key.char));
        },
      },
      doc,
    );
    node.classList.add('accent-bar__key');
    for (const event of ['pointerdown', 'mousedown']) {
      node.addEventListener(event, (e) => e.preventDefault());
    }
    bar.append(node);
  }

  return bar;
}

// ---------------------------------------------------------------------------------------------
// Where the software keyboard is
// ---------------------------------------------------------------------------------------------

/** The part of the visual viewport we need. Narrowed so a test can supply a plain object. */
export interface VisualViewportLike {
  height: number;
  offsetTop: number;
  addEventListener?: (type: string, listener: () => void) => void;
  removeEventListener?: (type: string, listener: () => void) => void;
}

/**
 * Pixels of the layout viewport hidden by the software keyboard.
 *
 * On iOS the keyboard does not resize the layout viewport and does not affect `dvh`; it shrinks the
 * *visual* viewport, which is the only thing that reports it. Zero where there is no visual
 * viewport, which is the honest answer for a browser that will not say — and the safe one, since it
 * leaves the bar in the flow where the layout already accounts for it.
 *
 * Clamped at zero: pinch-zoom makes the visual viewport smaller than the layout viewport with no
 * keyboard involved, and a browser mid-scroll can report a sum slightly over the layout height.
 */
export function keyboardInsetPx(
  visual: VisualViewportLike | null | undefined,
  layoutHeight: number,
): number {
  if (!visual) return 0;
  return Math.max(0, Math.round(layoutHeight - (visual.height + visual.offsetTop)));
}

/**
 * A keyboard is "up" only once it covers a real share of the screen.
 *
 * Some Android browsers report a few pixels of inset from their own toolbar animation, and a bar
 * that unpinned and repinned over that would flicker under the learner's thumb.
 */
export const KEYBOARD_UP_PX = 120;

/**
 * Publish the keyboard inset as `--keyboard-inset` and `data-keyboard` on the root element.
 *
 * A custom property rather than inline styles on the bar: the stylesheet then owns *what* moves,
 * which keeps the accent bar's layout in `base.css` with the rest of the card and lets anything else
 * that needs to dodge the keyboard read the same number.
 */
export class KeyboardInsetWatcher {
  private readonly onChange = () => this.publish();
  private started = false;

  constructor(
    private readonly win: Window,
    private readonly root: HTMLElement = win.document.documentElement,
  ) {}

  private get visual(): VisualViewportLike | null {
    return (this.win as Window & { visualViewport?: VisualViewportLike }).visualViewport ?? null;
  }

  publish(): number {
    const inset = keyboardInsetPx(this.visual, this.win.document.documentElement.clientHeight);
    this.root.style.setProperty('--keyboard-inset', `${inset}px`);
    this.root.dataset['keyboard'] = inset >= KEYBOARD_UP_PX ? 'up' : 'down';
    return inset;
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.publish();
    const visual = this.visual;
    // Both events: the keyboard opening is a resize, and scrolling with it open moves `offsetTop`.
    visual?.addEventListener?.('resize', this.onChange);
    visual?.addEventListener?.('scroll', this.onChange);
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    const visual = this.visual;
    visual?.removeEventListener?.('resize', this.onChange);
    visual?.removeEventListener?.('scroll', this.onChange);
  }
}

// ---------------------------------------------------------------------------------------------
// The physical keyboard
// ---------------------------------------------------------------------------------------------

export type KeyAction =
  | 'start-run'
  | 'submit-setup'
  | 'submit-answer'
  | 'submit-encoding'
  | 'finish-teaching'
  | 'continue'
  | 'to-entry'
  | 'end-run';

export interface KeyContext {
  phase: Phase;
  runPhase: RunPhase;
  /** The machine will take an answer right now. Asked of the machine, never inferred from focus. */
  acceptsAnswer: boolean;
  /** Focus is in a text field. */
  inField: boolean;
  /** Focus is on a button, which activates itself — we must not fire the action twice. */
  onButton: boolean;
}

/**
 * What a key press means right now (SPEC §4: Enter submits, Escape ends the run and banks).
 *
 * Pure, and a total function over the phases, so "what does Enter do on the reveal card?" is
 * answerable without a browser. Two rules in here are easy to lose in a listener and expensive to
 * lose in the dark:
 *
 *  - **Space is a character inside a field.** Half the answers in this game are more than one word,
 *    and a Space that submitted would bank `buenos` for `buenos días` — scored as a failure of
 *    Spanish rather than of the interface.
 *  - **Escape ends the run from a field too.** It is the one binding that must work whatever has
 *    focus, because it is the way out, and every completed item is already banked (DoD-5).
 */
export function keyAction(key: string, ctx: KeyContext): KeyAction | null {
  if (key === 'Escape') return ctx.phase === 'run' ? 'end-run' : null;
  if (key !== 'Enter' && key !== ' ') return null;
  if (ctx.onButton) return null;
  if (ctx.inField && key !== 'Enter') return null;

  switch (ctx.phase) {
    case 'entry':
      return 'start-run';
    case 'setup':
      return 'submit-setup';
    case 'reveal':
      return 'to-entry';
    case 'run':
      if (ctx.acceptsAnswer) return 'submit-answer';
      // The encoding step has a field of its own, and Enter has to reach it: it is the one card in
      // the run that is not an answer, and a learner who cannot get off it is stuck (invariant 5).
      if (ctx.runPhase === 'encoding') return 'submit-encoding';
      // The first-exposure card is the other one in a run that is not an answer (T59c).
      if (ctx.runPhase === 'teaching') return 'finish-teaching';
      return ctx.runPhase === 'feedback' ? 'continue' : null;
    default:
      return null;
  }
}
